import { Alert, Box, Button, Card, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useMemo } from 'react';

import { authStore, type DashboardAuth } from '../../auth/authStore';
import { createOnboardingApi, type OnboardingApi } from '../../onboarding/onboardingApi';
import { buildCanvasserOnboardingUrl } from '../../onboarding/onboardingUrl';
import {
  QR_REFRESH_INTERVAL_MS,
  useCanvasserOnboardingQr,
  type UseCanvasserOnboardingQrOptions,
} from '../../onboarding/useCanvasserOnboardingQr';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';

export type OnboardCanvassersPageProps = {
  auth: DashboardAuth;
  api?: Pick<OnboardingApi, 'issueToken'>;
  lifecycleOptions?: Pick<UseCanvasserOnboardingQrOptions, 'clock' | 'scheduler' | 'visibilityTarget'>;
};

const elapsedSeconds = (elapsedMs: number): number =>
  Math.min(QR_REFRESH_INTERVAL_MS / 1_000, Math.floor(elapsedMs / 1_000));

const QrRefreshProgress = ({ elapsedMs }: { elapsedMs: number }) => {
  const seconds = elapsedSeconds(elapsedMs);
  const progress = (elapsedMs / QR_REFRESH_INTERVAL_MS) * 100;

  return (
    <LinearProgress
      variant="determinate"
      value={progress}
      aria-label="QR code refresh progress"
      aria-valuemin={0}
      aria-valuemax={QR_REFRESH_INTERVAL_MS / 1_000}
      aria-valuenow={seconds}
      aria-valuetext={`${seconds} of 5 seconds elapsed`}
      sx={{ borderRadius: 0.5 }}
    />
  );
};

export const OnboardCanvassersPage = ({ auth, api, lifecycleOptions }: OnboardCanvassersPageProps) => {
  const onboardingApi = useMemo<Pick<OnboardingApi, 'issueToken'>>(() => {
    if (api) return api;

    let resolvedApi: OnboardingApi | undefined;
    const getApi = (): OnboardingApi => {
      resolvedApi ??= createOnboardingApi({ env: resolveClientEnv() });
      return resolvedApi;
    };

    return {
      issueToken: (input, options) => getApi().issueToken(input, options),
    };
  }, [api]);
  const { state, retry } = useCanvasserOnboardingQr({
    api: onboardingApi,
    brokerageId: auth.brokerageId,
    ...lifecycleOptions,
  });

  const signOut = (): void => authStore.clearAuth();

  return (
    <>
      <PageHeader
        title="Onboard Canvassers"
        subtitle="Ask each canvasser to scan this code, then have them complete the secure onboarding form on their device."
      />
      {state.status === 'loading' && <LoadingState message="Creating a secure onboarding QR code…" />}
      {state.status === 'unavailable' && (
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
          <ErrorState
            title="QR code unavailable"
            message={state.message}
            onRetry={retry}
          />
          {state.reason === 'missing-brokerage' && (
            <Button variant="outlined" onClick={signOut}>
              Sign out
            </Button>
          )}
        </Stack>
      )}
      {state.status === 'ready' && (
        <Box sx={{ width: '100%', maxWidth: 560, mx: { xs: 0, md: 'auto' } }}>
          <Stack spacing={2}>
            {state.refreshError && (
              <Alert
                severity="warning"
                action={
                  state.isRefreshing ? undefined : (
                    <Button color="inherit" size="small" onClick={retry}>
                      Retry
                    </Button>
                  )
                }
              >
                {state.refreshError}
              </Alert>
            )}
            <Card variant="outlined">
              <CardContent sx={{ pb: 3 }}>
                <Stack spacing={3} sx={{ alignItems: 'center' }}>
                  <Typography align="center" color="text.secondary">
                    The code refreshes automatically every five seconds. Keep this screen open while the canvasser scans it.
                  </Typography>
                  <Box
                    role="img"
                    aria-label="QR code for canvasser onboarding"
                    sx={{ width: 'min(100%, 320px)', aspectRatio: '1 / 1', bgcolor: 'common.white', p: 1 }}
                  >
                    <QRCodeSVG
                      value={buildCanvasserOnboardingUrl(state.token)}
                      title="Canvasser onboarding QR code"
                      level="H"
                      includeMargin
                      fgColor="#000000"
                      bgColor="#ffffff"
                      size={320}
                      style={{ display: 'block', width: '100%', height: '100%' }}
                    />
                  </Box>
                </Stack>
              </CardContent>
              <QrRefreshProgress elapsedMs={state.elapsedMs} />
            </Card>
          </Stack>
        </Box>
      )}
    </>
  );
};
